import { useEffect, useState } from "react";
import {
  useCurrentCommunityMember,
  useCurrentPostContext,
} from "@circle-react/contexts";
import type { Space } from "@circle-react/types";
import type { Comment } from "@circle-react/types/Comment";
import { useJumpToComment } from "../useJumpToComment";
import { useCommentsInfiniteQuery } from "./useCommentsInfiniteQuery";
import { useCommentsListManagement } from "./useCommentsListManagement";
import { useCommentsPersistentCache } from "./useCommentsPersistentCache";
import { useCommentsSectionFocus } from "./useCommentsSectionFocus";

interface CommentsListParams {
  isUsedInMinimalTipTapViewModal?: boolean;
  onCommentCreate?: (comment: Comment) => void;
  onCommentDelete?: (comment: Comment) => void;
  parentCommentId: string | number | null;
  postCommentsClosed?: boolean;
  postId: string | number;
  postLikingDisabled?: boolean;
  preloaded?: boolean;
  preloadedComments?: Comment[];
  preloadedCommentsTotalCount?: number;
  resetCommunityMemberForReply?: () => void;
  usedIn?: string;
}

export const useCommentsList = ({
  postId,
  parentCommentId,
  preloaded = false,
  preloadedComments = [],
  preloadedCommentsTotalCount = 0,
  postCommentsClosed,
  postLikingDisabled,
  usedIn: usedInProp = "post",
  isUsedInMinimalTipTapViewModal: isUsedInMinimalTipTapViewModalProp = false,
  onCommentCreate,
  onCommentDelete,
  resetCommunityMemberForReply,
  space,
  showReplyForm = false,
  commentsSectionRef,
}: CommentsListParams & {
  space?: Space;
  showReplyForm?: boolean;
  commentsSectionRef?: React.RefObject<HTMLElement>;
}) => {
  const currentCommunityMember = useCurrentCommunityMember();
  const {
    usedIn = usedInProp,
    isUsedInMinimalTipTapViewModal = isUsedInMinimalTipTapViewModalProp,
  } = useCurrentPostContext();

  const {
    comments,
    setComments,
    addCommentToList,
    updateCommentInList,
    removeCommentFromList,
  } = useCommentsListManagement({ initialComments: preloadedComments || [] });

  const [areCommentsClosed, setAreCommentsClosed] =
    useState(postCommentsClosed);
  const [isLikingDisabled, setIsLikingDisabled] = useState(postLikingDisabled);
  const [formKey, setFormKey] = useState(0);

  const { shouldFocusOnCommentsSection, setShouldFocusOnCommentsSection } =
    useCommentsSectionFocus();

  const { persistCacheKey, resetPersistedFormValues } =
    useCommentsPersistentCache({
      postId,
      parentCommentId,
    });

  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, remove } =
    useCommentsInfiniteQuery({
      postId,
      parentCommentId,
      usedIn,
      preloaded,
      preloadedComments,
      preloadedCommentsTotalCount,
      onCommentsUpdate: setComments,
    });

  const didPermissionsChange =
    postCommentsClosed !== areCommentsClosed ||
    postLikingDisabled !== isLikingDisabled;

  // Fully reload comments when closing/opening comments and disabling/enabling
  // likes. This ensures that permissions are correctly updated in the UI.
  useEffect(() => {
    if (!preloaded && didPermissionsChange) {
      setAreCommentsClosed(postCommentsClosed);
      setIsLikingDisabled(postLikingDisabled);
      setComments([]);
      remove();
    }
  }, [
    preloaded,
    didPermissionsChange,
    postCommentsClosed,
    postLikingDisabled,
    remove,
    setComments,
  ]);

  const handleCommentCreate = (comment: Comment) => {
    resetCommunityMemberForReply?.();
    addCommentToList(comment);
    onCommentCreate?.(comment);

    const newCommentElement = document.getElementById(
      `comment_wrapper_${comment.id}`,
    );

    if (newCommentElement) {
      newCommentElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    resetPersistedFormValues();
    setFormKey(prev => prev + 1);
  };

  const handleCommentDelete = (comment: Comment) => {
    removeCommentFromList(comment);
    onCommentDelete?.(comment);
  };

  const isFirstLoading = isLoading && comments.length === 0;
  const isAnyLoading = isLoading || isFetchingNextPage;

  useEffect(() => {
    if (!isAnyLoading && shouldFocusOnCommentsSection) {
      commentsSectionRef?.current?.scrollIntoView({ behavior: "smooth" });
      setShouldFocusOnCommentsSection(false);
    }
  }, [
    isAnyLoading,
    shouldFocusOnCommentsSection,
    setShouldFocusOnCommentsSection,
    commentsSectionRef?.current,
  ]);

  const shouldShowForm =
    currentCommunityMember &&
    space &&
    showReplyForm &&
    (!isAnyLoading || comments.length > 0);

  useJumpToComment({ enabled: !isLoading });

  return {
    comments,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    addCommentToList,
    updateCommentInList,
    removeCommentFromList,
    handleCommentCreate,
    handleCommentDelete,
    formKey,
    persistCacheKey,
    isFirstLoading,
    isAnyLoading,
    shouldShowForm,
    usedIn,
    isUsedInMinimalTipTapViewModal,
  };
};
